// Shared offering normalizer.
//
// Both the "Import JSON" dialog and the AI Offering Builder need to take a
// loosely-shaped object (snake_case OR camelCase, possibly wrapped in an
// `{ offer: {...} }` envelope, possibly with missing groups or invalid enum
// values) and turn it into a complete, form-ready `OfferFormValue`.
//
// The form is the single source of truth for shape + allowed enum values, so
// this util imports `emptyOffer()` and the enum lists directly from the form
// module. That keeps the normalizer in lockstep with the form rather than
// duplicating the schema.
import {
  emptyOffer,
  OFFER_TYPES,
  PAIN_INTENSITIES,
  TIME_TO_VALUE,
  PRICING_MODELS,
  DELIVERY_MODELS,
  DELIVERY_LOCATIONS,
  PRIMARY_CTAS,
  SERVICE_TYPES,
  SERVICE_FORMATS,
  type OfferFormValue,
} from "./offer-template";

// Top-level snake_case / camelCase → OfferFormValue key.
export const SNAKE_TO_CAMEL: Record<string, keyof OfferFormValue> = {
  name: "name",
  slug: "slug",
  status: "status",
  offer_type: "offerType",
  offerType: "offerType",
  offer_category: "offerCategory",
  offerCategory: "offerCategory",
  short_description: "shortDescription",
  shortDescription: "shortDescription",
  summary: "summary",
  buyer_problem: "buyerProblem",
  buyerProblem: "buyerProblem",
  desired_outcome: "desiredOutcome",
  desiredOutcome: "desiredOutcome",
  value_proposition: "valueProposition",
  valueProposition: "valueProposition",
  offer_details: "offerDetails",
  offerDetails: "offerDetails",
  fulfillment: "fulfillment",
  proof: "proof",
  risk_reversal: "riskReversal",
  riskReversal: "riskReversal",
  objections: "objections",
  conversion: "conversion",
  persona_fit: "personaFit",
  personaFit: "personaFit",
  creative_inputs: "creativeInputs",
  creativeInputs: "creativeInputs",
  service_details: "serviceDetails",
  serviceDetails: "serviceDetails",
};

const isObj = (v: any): v is Record<string, any> =>
  !!v && typeof v === "object" && !Array.isArray(v);

// snake_case → camelCase (for tolerating camelCase sub-keys from the AI).
const toCamelKey = (s: string) => s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

// Coerce an arbitrary value to one of `allowed`, normalizing whitespace/case
// (e.g. "Done For You" → "done_for_you") and matching near-misses (aliases,
// prefixes, containment — e.g. "Monthly subscription" → "subscription",
// "day" → "days") so AI output isn't silently dropped. A non-empty value that
// still can't be matched lands on "other" when the enum has that bucket.
// Returns undefined only when there's nothing usable, so callers can fall
// back to the form default.
function coerceEnum(value: any, allowed: string[]): string | undefined {
  if (typeof value !== "string") return undefined;
  const raw = value.trim();
  if (!raw) return undefined;
  if (allowed.includes(raw)) return raw;
  const norm = raw
    .toLowerCase()
    .replace(/[\s\-\/]+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
  if (allowed.includes(norm)) return norm;
  const flatOf = (s: string) => s.replace(/_/g, "");
  const flat = flatOf(norm);
  const exactFlat = allowed.find((a) => flatOf(a) === flat);
  if (exactFlat) return exactFlat;
  // Near-miss: the value contains an allowed value ("monthly subscription" →
  // subscription) or is a prefix of one ("day" → days). Longest match wins;
  // "other" never wins a containment match.
  const near = allowed
    .filter((a) => a !== "other")
    .filter((a) => {
      const af = flatOf(a);
      if (af.length >= 4 && flat.includes(af)) return true;
      if (flat.length >= 3 && af.startsWith(flat)) return true;
      return false;
    })
    .sort((a, b) => flatOf(b).length - flatOf(a).length);
  if (near.length) return near[0];
  if (allowed.includes("other")) return "other";
  return undefined;
}

// Map a loose object's top-level keys onto OfferFormValue keys.
function mapTopLevel(root: Record<string, any>): Partial<OfferFormValue> {
  const out: any = {};
  for (const k of Object.keys(root)) {
    const mapped = SNAKE_TO_CAMEL[k];
    if (mapped) out[mapped] = root[k];
  }
  return out;
}

// Number of recognizable offer field groups in a loose object — used by the
// import dialog to decide whether there's anything worth importing.
export function countRecognizedOfferKeys(raw: any): number {
  if (!isObj(raw)) return 0;
  const root = isObj(raw.offer) ? raw.offer : raw;
  return Object.keys(mapTopLevel(root)).length;
}

// Deep-merge a single object group over a base group, coercing enum sub-fields.
// Only keys present on the base are kept (drops anything the AI/JSON invented),
// which keeps the merged object aligned with what the form actually renders.
// camelCase variants of the base's snake_case keys are accepted so AI output
// that drifted in casing still lands in the form instead of vanishing.
function mergeGroup(
  base: Record<string, any>,
  incoming: any,
  enumFields: Record<string, string[]> = {},
): Record<string, any> {
  if (!isObj(incoming)) return base;
  const out: Record<string, any> = { ...base };
  for (const key of Object.keys(base)) {
    const srcKey = key in incoming ? key : toCamelKey(key) in incoming ? toCamelKey(key) : undefined;
    if (srcKey === undefined) continue;
    const val = incoming[srcKey];
    const allowed = enumFields[key];
    if (allowed) {
      const coerced = coerceEnum(val, allowed);
      if (coerced !== undefined) out[key] = coerced;
      // unusable enum value → keep the base default
    } else {
      out[key] = val;
    }
  }
  return out;
}

// Normalize an array of object entries (tiers / objections / persona fit)
// against a known entry template: accepts camelCase key variants, coerces bare
// strings into an entry with the primary text field set, and drops entries
// with no usable content. Returns undefined when `arr` isn't an array so
// callers can keep their previous value.
function normalizeEntryList(
  arr: any,
  template: Record<string, string>,
): Record<string, string>[] | undefined {
  if (!Array.isArray(arr)) return undefined;
  const keys = Object.keys(template);
  const out: Record<string, string>[] = [];
  for (const e of arr) {
    if (typeof e === "string") {
      if (e.trim()) out.push({ ...template, [keys[0]]: e.trim() });
      continue;
    }
    if (!isObj(e)) continue;
    const entry: Record<string, string> = { ...template };
    let hasContent = false;
    for (const k of keys) {
      const srcKey = k in e ? k : toCamelKey(k) in e ? toCamelKey(k) : undefined;
      if (srcKey === undefined) continue;
      const v = e[srcKey];
      if (typeof v === "string") {
        entry[k] = v;
        if (v.trim()) hasContent = true;
      } else if (v != null) {
        entry[k] = String(v);
        hasContent = true;
      }
    }
    if (hasContent) out.push(entry);
  }
  return out;
}

const TIER_TEMPLATE = { tier_name: "", description: "", price_or_price_range: "", best_for: "" };
const OBJECTION_TEMPLATE = { objection: "", response: "", proof_to_use: "" };
const PERSONA_FIT_TEMPLATE = {
  persona_name: "",
  why_they_need_it: "",
  main_pain: "",
  main_desire: "",
  likely_objection: "",
  best_angle: "",
};

/**
 * Normalize a loose offer-shaped object into a complete `OfferFormValue`.
 *
 * - Accepts snake_case or camelCase keys, and an `{ offer: {...} }` envelope.
 * - Merges over `emptyOffer()` so every group/array the form expects exists.
 * - When `current` is supplied (e.g. the user already typed something before
 *   importing), incoming fields merge over the current values instead of being
 *   discarded.
 * - Coerces enum fields to valid values; invalid ones fall back to defaults.
 * - Guarantees the two required fields: `name` (string) and `offerType`.
 */
export function normalizeOfferInput(
  raw: any,
  current?: OfferFormValue,
): OfferFormValue {
  const base = emptyOffer();
  const cur: Partial<OfferFormValue> = isObj(current) ? current : {};
  const root = isObj(raw?.offer) ? raw.offer : isObj(raw) ? raw : {};
  const mapped = mapTopLevel(root);

  // Start from base + current so untouched fields keep their current values.
  const next: OfferFormValue = { ...base, ...(cur as OfferFormValue) };

  // Scalars
  if (typeof mapped.name === "string" && mapped.name.trim()) next.name = mapped.name;
  next.name = next.name ?? "";

  const ot = coerceEnum(mapped.offerType, OFFER_TYPES);
  next.offerType = ot ?? next.offerType ?? "service";

  if (mapped.slug != null) next.slug = mapped.slug as any;
  if (mapped.status != null) next.status = mapped.status as any;
  if (mapped.offerCategory != null) next.offerCategory = mapped.offerCategory as any;
  if (mapped.summary != null) next.summary = mapped.summary as any;
  if (mapped.shortDescription != null) next.shortDescription = mapped.shortDescription as any;

  // Object groups — merge over (current group ?? base group) so partial input
  // fills gaps with defaults and never clobbers untouched current edits.
  const curGroup = (k: keyof OfferFormValue) => ({
    ...(base as any)[k],
    ...(isObj((cur as any)[k]) ? (cur as any)[k] : {}),
  });

  next.buyerProblem = mergeGroup(curGroup("buyerProblem"), mapped.buyerProblem, {
    pain_intensity: PAIN_INTENSITIES,
  });
  next.desiredOutcome = mergeGroup(curGroup("desiredOutcome"), mapped.desiredOutcome, {
    time_to_value: TIME_TO_VALUE,
  });
  next.valueProposition = mergeGroup(curGroup("valueProposition"), mapped.valueProposition);
  next.offerDetails = mergeGroup(curGroup("offerDetails"), mapped.offerDetails, {
    pricing_model: PRICING_MODELS,
  });
  next.fulfillment = mergeGroup(curGroup("fulfillment"), mapped.fulfillment, {
    delivery_model: DELIVERY_MODELS,
    delivery_location: DELIVERY_LOCATIONS,
  });
  next.proof = mergeGroup(curGroup("proof"), mapped.proof);
  next.riskReversal = mergeGroup(curGroup("riskReversal"), mapped.riskReversal);
  next.conversion = mergeGroup(curGroup("conversion"), mapped.conversion, {
    primary_cta: PRIMARY_CTAS,
  });
  next.creativeInputs = mergeGroup(curGroup("creativeInputs"), mapped.creativeInputs);
  next.serviceDetails = mergeGroup(curGroup("serviceDetails"), mapped.serviceDetails, {
    service_type: SERVICE_TYPES,
    service_format: SERVICE_FORMATS,
  });

  // Tiers ride inside offerDetails — normalize entry keys/shapes so camelCase
  // or string entries from the AI still render in the tier cards.
  const tiers = normalizeEntryList(next.offerDetails?.packages_or_tiers, TIER_TEMPLATE);
  if (tiers) next.offerDetails = { ...next.offerDetails, packages_or_tiers: tiers };

  // Array groups — replace only when the incoming value is actually an array.
  const objections = normalizeEntryList(mapped.objections, OBJECTION_TEMPLATE);
  if (objections) next.objections = objections;
  const personaFit = normalizeEntryList(mapped.personaFit, PERSONA_FIT_TEMPLATE);
  if (personaFit) next.personaFit = personaFit;

  return next;
}
